import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { delta } from '../lib/format'

/**
 * Jamaican dollar figures run long — a layby balance in the hundreds of
 * millions is sixteen characters before the currency prefix — so the value
 * steps down a size or two rather than overflowing its tile.
 */
function valueClass(value: ReactNode): string {
  if (typeof value !== 'string') return 'value'
  if (value.length > 15) return 'value tiny'
  if (value.length > 12) return 'value small'
  return 'value'
}

export function Kpi({
  label,
  value,
  foot,
  to,
}: {
  label: string
  value: ReactNode
  foot?: ReactNode
  /** Makes the whole tile a link through to whatever the number counts. */
  to?: string
}) {
  const body = (
    <>
      <div className="label">{label}</div>
      <div className={valueClass(value)} title={typeof value === 'string' ? value : undefined}>
        {value}
      </div>
      {foot ? <div className="foot">{foot}</div> : null}
      {to ? <div className="kpi-more">See the {String(label).toLowerCase()} →</div> : null}
    </>
  )
  if (!to) return <div className="kpi">{body}</div>
  return (
    <Link className="kpi linked" to={to}>
      {body}
    </Link>
  )
}

/**
 * Period-over-period change. `inverse` is for figures where down is good —
 * refunds, for instance — so the colour still means "good" or "bad".
 */
export function Trend({
  current,
  previous,
  inverse = false,
}: {
  current: number
  previous: number
  inverse?: boolean
}) {
  if (!previous) {
    // No baseline to compare against; a percentage would be meaningless.
    return <span className="trend flat">no prior data</span>
  }
  const change = ((current - previous) / Math.abs(previous)) * 100
  const rounded = Math.abs(change) < 0.05 ? 0 : change
  const better = inverse ? rounded < 0 : rounded > 0
  const tone = rounded === 0 ? 'flat' : better ? 'up' : 'down'
  const arrow = rounded === 0 ? '' : rounded > 0 ? '▲' : '▼'
  return (
    <span className={`trend ${tone}`}>
      {arrow} {delta(rounded)}
    </span>
  )
}
